import ArticleLayout from "@/components/feature/ArticleLayout";
import { Link } from "react-router-dom";

const faqs = [
  { q: "Ist OOPBUY wirklich sicher für Online-Einkäufe?", a: "Ja, OOPBUY ist eine etablierte Plattform mit Sicherheitsmechanismen wie Verkäuferbewertungen und QC-Bildern. Mit dem OOPBUY Spreadsheet hast du alle Sicherheitsinformationen zentral im Blick." },
  { q: "Was passiert, wenn ein Produkt nicht ankommt?", a: "OOPBUY bietet Käuferschutz. Wenn ein Produkt nicht ankommt, kannst du einen Fall eröffnen. Das OOPBUY Spreadsheet dokumentiert alle relevanten Bestellinformationen." },
  { q: "Wie erkenne ich betrügerische Verkäufer?", a: "Das OOPBUY Spreadsheet zeigt dir Verkäuferbewertungen und Transaktionshistorien an. Verkäufer mit weniger als 90% positiven Bewertungen solltest du meiden." },
  { q: "Sind meine Zahlungsdaten bei OOPBUY sicher?", a: "OOPBUY verwendet verschlüsselte Zahlungsabwicklung. Gib niemals Zahlungsdaten außerhalb der offiziellen Plattform weiter." },
];

const checklistItems = [
  "Verkäuferprofil vollständig prüfen (Bewertungen, Alter des Accounts, Anzahl Transaktionen)",
  "Produktbeschreibung Wort für Wort lesen — achte auf versteckte Hinweise",
  "QC-Bilder immer anfordern und genau inspizieren",
  "Niemals außerhalb der OOPBUY-Plattform kommunizieren oder bezahlen",
  "Verdächtige Angebote (zu günstig, zu gut um wahr zu sein) sofort melden",
];

export default function SicherheitGuidePage() {
  return (
    <ArticleLayout
      title1="OOPBUY Sicherheit"
      title2="& Datenschutz"
      title3="Der komplette Guide"
      date="13. Juli 2026"
      readTime="10 Minuten"
      tag="Sicherheit"
      seoDescription="Der umfassende OOPBUY Sicherheit & Datenschutz Guide. Erfahre, wie du mit dem OOPBUY Spreadsheet Betrug vermeidest, Verkäufer prüfst und deine persönlichen Daten beim Online-Shopping schützt."
      faqs={faqs}
    >
      <p className="text-lg text-foreground-700 leading-relaxed">
        Sicherheit steht beim Online-Shopping an erster Stelle — besonders wenn du über Plattformen wie OOPBUY einkaufst. In diesem Guide erfährst du, wie du das <strong>OOPBUY Spreadsheet</strong> nutzen kannst, um sicherer einzukaufen, Betrug zu vermeiden und deine persönlichen Daten zu schützen.
      </p>

      <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4">
        Wie das OOPBUY Spreadsheet deine Sicherheit erhöht
      </h2>
      <p className="text-foreground-700 leading-relaxed">
        Das <strong>OOPBUY Spreadsheet</strong> ist nicht nur ein Tool zur Produktfindung — es ist auch ein Sicherheitsinstrument. Indem es alle relevanten Verkäuferinformationen, Bewertungen und Produktdetails an einem Ort bündelt, hilft es dir, fundierte und sichere Kaufentscheidungen zu treffen. Du siehst auf einen Blick, welche Verkäufer vertrauenswürdig sind und welche Produkte eine hohe Qualität aufweisen.
      </p>

      <div className="my-6">
        <img
          src="https://readdy.ai/api/search-image?query=Modern%20secure%20online%20shopping%20concept%20with%20laptop%20displaying%20safety%20shield%20icon%20and%20product%20catalog%2C%20clean%20white%20desk%20setup%2C%20soft%20natural%20light%2C%20minimalist%20professional%20workspace%2C%20calm%20atmosphere%2C%20sage%20green%20accents%2C%20editorial%20technology%20photography%2C%20fresh%20modern%20aesthetic%2C%20reassuring%20composition&width=1200&height=700&seq=article-sicherheit-security-v2&orientation=landscape"
          alt="OOPBUY Sicherheit und Datenschutz"
          className="w-full rounded-xl object-cover"
        />
      </div>

      <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4">
        Die 5 wichtigsten Sicherheits-Checkpoints
      </h2>
      <div className="space-y-4 my-6">
        {checklistItems.map((item, i) => (
          <div key={i} className="bg-background-100 rounded-xl p-5 border border-foreground-100/30">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-accent-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">{i + 1}</div>
              <p className="text-sm text-foreground-700 leading-relaxed pt-1">{item}</p>
            </div>
          </div>
        ))}
      </div>

      <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4">
        Verkäufer-Bewertungen richtig lesen
      </h2>
      <p className="text-foreground-700 leading-relaxed">
        Das <strong>OOPBUY Spreadsheet</strong> enthält detaillierte Verkäuferbewertungen, die dir helfen, seriöse von unseriösen Anbietern zu unterscheiden. Achte nicht nur auf die Gesamtbewertung, sondern auch auf die Anzahl der Bewertungen und das Alter des Verkäufer-Accounts. Ein neuer Account mit wenigen, aber perfekten Bewertungen kann ein Warnsignal sein.
      </p>

      <div className="overflow-x-auto my-6">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-foreground-200">
              <th className="text-left py-3 px-4 text-sm font-semibold text-foreground-800">Kriterium</th>
              <th className="text-center py-3 px-4 text-sm font-semibold text-primary-700">Sicher</th>
              <th className="text-center py-3 px-4 text-sm font-semibold text-foreground-700">Vorsicht</th>
            </tr>
          </thead>
          <tbody>
            {[
              ["Bewertung", "≥ 95% positiv", "< 90% positiv"],
              ["Transaktionen", "500+", "< 50"],
              ["Account-Alter", "1 Jahr+", "< 3 Monate"],
              ["Kommunikation", "Schnell & professionell", "Unprofessionell"],
            ].map((row, i) => (
              <tr key={i} className="border-b border-foreground-100">
                <td className="py-3 px-4 text-sm text-foreground-700">{row[0]}</td>
                <td className="py-3 px-4 text-sm text-primary-600 text-center font-medium">{row[1]}</td>
                <td className="py-3 px-4 text-sm text-foreground-500 text-center">{row[2]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4">
        Datenschutz beim Einkauf mit OOPBUY
      </h2>
      <p className="text-foreground-700 leading-relaxed">
        Deine persönlichen Daten sind wertvoll. Das <strong>OOPBUY Spreadsheet</strong> selbst sammelt keine personenbezogenen Daten, aber bei der Nutzung der OOPBUY-Plattform gibst du Informationen preis. Teile nur die absolut notwendigen Daten und nutze nach Möglichkeit die von OOPBUY bereitgestellten Datenschutzfunktionen. Vermeide es, sensible Informationen wie Ausweisnummern oder Kreditkartendetails in Chat-Nachrichten zu teilen.
      </p>

      <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4">
        QC-Bilder als Sicherheitsnetz
      </h2>
      <p className="text-foreground-700 leading-relaxed">
        QC-Bilder (Quality Control) sind dein wichtigstes Sicherheitsnetz beim Einkauf über OOPBUY. Bevor deine Bestellung versendet wird, macht das OOPBUY-Team Fotos der tatsächlichen Produkte. Das <strong>OOPBUY Spreadsheet</strong> erinnert dich daran, QC-Bilder anzufordern, und zeigt dir, bei welchen Produkten dies besonders empfehlenswert ist. Prüfe die Bilder genau: Stimmen Farbe, Größe und Zustand mit der Produktbeschreibung überein?
      </p>

      <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4">
        Häufige Fragen zu Sicherheit & Datenschutz
      </h2>
      <div className="space-y-4 my-6">
        {faqs.map((faq, i) => (
          <div key={i} className="bg-background-100 rounded-xl p-5 border border-foreground-100/30">
            <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2">{faq.q}</h3>
            <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
          </div>
        ))}
      </div>

      <div className="mt-10 pt-8 border-t border-foreground-200">
        <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-4">Weiterführende Ressourcen</h3>
        <div className="flex flex-wrap gap-3">
          <Link to="/" className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-50 text-primary-700 rounded-full text-sm font-medium hover:bg-primary-100 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-home-line w-4 h-4 flex items-center justify-center"></i>
            Zur Startseite
          </Link>
          <Link to="/einkaufsguide" className="inline-flex items-center gap-1.5 px-4 py-2 bg-background-100 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-shopping-bag-line w-4 h-4 flex items-center justify-center"></i>
            Einkaufsguide
          </Link>
        </div>
      </div>
    </ArticleLayout>
  );
}